import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import Button from '../../../components/ui/Button'

interface TicketIdentifierSearchProps {
  /** Uygulanmış (committed) tam-kimlik terimi (tam PNR veya tam Ad-Soyad). */
  value: string
  /** "Ara" ile taslağı uygular (kırpılmış). */
  onSearch: (term: string) => void
  /** Tam-kimlik aramasını temizler (yalnız kendi kayıtlarına döner). */
  onClear: () => void
}

/**
 * Özel kalem personeli (037) tam-kimlik araması — tek, kompakt kutu (filtre çubuğunda normal aramanın
 * yanında). Tam PNR veya tam Ad-Soyad yazıp "Ara"ya (ya da Enter'a) basınca o kimliğe uyan (başka
 * personelin dahil) talepler listelenir; kutu boşken yalnız kendi kayıtları. Girilen terim backend'e hem
 * PNR hem Ad-Soyad tam-eşleşmesi olarak denenir (OR). İçindeki × ile temizlenir (yalnız kendine döner).
 * Yalnız {@code TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER} yetkisi olan kullanıcıya render edilir; asıl koruma
 * backend'de (tam eşleşme yoksa kendi-dışı kayıt gelmez).
 */
export default function TicketIdentifierSearch({
  value,
  onSearch,
  onClear,
}: Readonly<TicketIdentifierSearchProps>) {
  const { t } = useTranslation()
  const [term, setTerm] = useState(value)

  // Uygulanmış terim (value) dışarıdan değişince (ör. "Filtreleri Temizle" → '') kutuyu senkronla;
  // aksi halde committed exact boşalır ama input bayat metni gösterir (ve Ara bayat terimi yeniden uygular).
  useEffect(() => setTerm(value), [value])

  const submit = () => onSearch(term.trim())
  const clear = () => {
    setTerm('')
    onClear()
  }

  return (
    <span className="tk-identifier-search">
      <span className="tk-identifier-field">
        <InputText
          className="tk-identifier-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
          placeholder={t('tickets.identifier.placeholder')}
          aria-label={t('tickets.identifier.placeholder')}
        />
        {term.trim() !== '' && (
          <button
            type="button"
            className="tk-identifier-clear"
            aria-label={t('tickets.identifier.clear')}
            onClick={clear}
          >
            <i className="pi pi-times" />
          </button>
        )}
      </span>
      <Button icon="pi pi-search" label={t('tickets.identifier.search')} onClick={submit} />
    </span>
  )
}
